import type { ProfileDetailSchemaSocialProfilesItem } from "@/lib/api/model";

const STACKOVERFLOW_ID_RE = /stackoverflow\.com\/users\/(\d+)/i;
const STACKOVERFLOW_NETWORK_HINTS = ["stackoverflow", "stackexchange"];

/**
 * Pulls a numeric Stack Overflow user id out of a candidate's
 * `social_profiles` array. The frontend uses this to decide whether to
 * render the Stack Overflow block — and, if so, which user to fetch.
 *
 * We accept any social-profile entry whose `network` looks like
 * Stack Overflow (case-insensitive substring match) and whose `url`
 * matches `stackoverflow.com/users/{digits}`. Returns `null` when no
 * match is found.
 */
export function extractStackoverflowId(
  socialProfiles: ProfileDetailSchemaSocialProfilesItem[] | null | undefined,
): number | null {
  if (!socialProfiles || socialProfiles.length === 0) return null;

  for (const raw of socialProfiles) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Record<string, unknown>;
    const network = typeof entry.network === "string" ? entry.network.toLowerCase() : "";
    if (!network) continue;
    if (!STACKOVERFLOW_NETWORK_HINTS.some((hint) => network.includes(hint))) continue;
    const url = typeof entry.url === "string" ? entry.url : null;
    if (!url) continue;
    const match = url.match(STACKOVERFLOW_ID_RE);
    if (match && match[1]) {
      const id = Number.parseInt(match[1], 10);
      if (Number.isFinite(id) && id > 0) return id;
    }
  }

  return null;
}
